import { useCallback, useEffect, useRef, useState } from "react";
import { formatError } from "@/lib/api";

// Every async load MUST be able to FAIL. This hook enforces it — "a loading state that cannot
// fail is a bug." It gives: a hard 10s timeout, an error state carrying the reason, a retry(),
// and abort-on-unmount so a fast navigation never leaves a promise that resolves into a dead
// spinner. Pass a function that takes an AbortSignal and returns a promise of the data.
export function useAsyncLoad(fn, deps = [], { timeoutMs = 10000 } = {}) {
  const [status, setStatus] = useState("loading"); // "loading" | "ok" | "error"
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setStatus("loading");
    setError(null);
    const timer = setTimeout(() => {
      controller.abort();
      if (alive) {
        setError("Timed out after 10s — the server did not respond.");
        setStatus("error");
      }
    }, timeoutMs);

    Promise.resolve(fnRef.current(controller.signal))
      .then((result) => {
        if (!alive) return;
        clearTimeout(timer);
        setData(result);
        setStatus("ok");
      })
      .catch((err) => {
        if (!alive || controller.signal.aborted) return; // timeout path already set the error
        clearTimeout(timer);
        setError(formatError(err?.response?.data?.detail) || err?.message || "Request failed.");
        setStatus("error");
      });

    return () => { alive = false; clearTimeout(timer); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  return { status, data, error, retry, setData };
}

export default useAsyncLoad;
